import moment from "moment";
import { createStaticRanges, defaultStaticRanges } from "react-date-range";

export function getActiveBoards(boards = []) {
  return [...boards]
    .filter((board) => !board.is_archive)
    .sort((a, b) => a.board_name.localeCompare(b.board_name));
}

export function getMyTaskHeadingLabel(users = []) {
  if (users.length === 1) {
    return "My Tasks";
  }

  if (users.length < 1) {
    return "Select User";
  }

  return "Multiple";
}

export function isMyTaskQueryActive(location, query) {
  return (
    location.pathname.includes("/my-task") &&
    location.search.includes(query)
  );
}

export function getMyTaskNavItems() {
  return [
    {
      key: "mine-tab",
      to: "/my-task?tab=mine",
      label: "My Tasks",
      query: "?tab=mine",
    },
    {
      key: "watching-tab",
      to: "/watching",
      label: "Watching",
      pathname: "/watching",
    },
    {
      key: "team-tab",
      to: "/my-task?tab=team",
      label: "Task Assignments",
      query: "?tab=team",
    },
    {
      key: "worklog-tab",
      to: null,
      label: "Worklogs",
      query: "?tab=worklog",
      isWorklog: true,
    },
  ];
}

export function normalizeTeams(teamResponseData = []) {
  return teamResponseData.map((team) => ({
    ...team,
    users: (team.users || []).filter((user) => user.active),
  }));
}

export function getFilteredTeamUsers({
  users = [],
  teams = [],
  searchQuery = "",
}) {
  if (!searchQuery) {
    return users;
  }

  const normalizedQuery = searchQuery.toLowerCase();
  const matchingTeams = teams.filter((team) =>
    team.team.toLowerCase().includes(normalizedQuery)
  );

  if (matchingTeams.length > 0) {
    const matchingTeamIds = matchingTeams.map((team) => team.team_id);
    return users.filter((user) => matchingTeamIds.includes(user.team_id));
  }

  return users.filter(
    (user) =>
      user.first_name.toLowerCase().includes(normalizedQuery) ||
      user.last_name.toLowerCase().includes(normalizedQuery)
  );
}

export function getFilteredSelectableUsers(users = [], searchQuery = "") {
  if (!searchQuery) {
    return users;
  }

  const normalizedQuery = searchQuery.toLowerCase();

  return users.filter(
    (user) =>
      user.first_name.toLowerCase().includes(normalizedQuery) ||
      user.last_name.toLowerCase().includes(normalizedQuery)
  );
}

export function getFlattenedGroupingUsers(groupingUser = []) {
  return groupingUser.map((item) => item?.users || []).flat().filter(Boolean);
}

export function parseUserIdsParam(userParam) {
  if (!userParam) {
    return [];
  }

  return userParam
    .split(",")
    .filter(Boolean)
    .map((userId) => ({ user_id: Number(userId) }));
}

export function parseBoardIdsParam(boardParam) {
  if (!boardParam) {
    return [];
  }

  return boardParam
    .split(",")
    .filter(Boolean)
    .map((boardId) => Number(boardId));
}

export function buildGroupedTeams(groupingUsers = [], allTeams = []) {
  const teamsById = groupingUsers.reduce((accumulator, user) => {
    if (!user?.team_id) {
      return accumulator;
    }

    if (!accumulator[user.team_id]) {
      accumulator[user.team_id] = [];
    }

    accumulator[user.team_id].push(user);
    return accumulator;
  }, {});

  return Object.entries(teamsById).map(([teamId, users]) => ({
    team:
      allTeams.find((team) => team.team_id === Number(teamId))?.team || "",
    users,
  }));
}

export function getUnassignedUsers({
  filteredItems = [],
  selectedUsers = [],
}) {
  const selectedUserIds = new Set(selectedUsers.map((user) => user.user_id));

  return filteredItems
    .filter((user) => user.active && !user.team_id)
    .sort((a, b) => {
      const aSelected = selectedUserIds.has(a.user_id);
      const bSelected = selectedUserIds.has(b.user_id);

      if (aSelected !== bSelected) {
        return bSelected - aSelected;
      }

      return a.first_name.localeCompare(b.first_name);
    });
}

export function createExpandedTeamPanels(teams = [], isExpand = false) {
  return teams.map((team) => ({
    team_id: team.team_id,
    isExpand,
  }));
}

export function getSearchExpandedTeamPanels({
  teams = [],
  filteredItems = [],
}) {
  return teams.map((team) => ({
    team_id: team.team_id,
    isExpand: team.users.some((user) =>
      filteredItems.some((filteredUser) => filteredUser.user_id === user.user_id)
    ),
  }));
}

export function getDateRangeHeading(dateRange) {
  return dateRange
    ? `${moment(dateRange.startDate).format("MM/DD/YYYY")} - ${moment(
        dateRange.endDate
      ).format("MM/DD/YYYY")}`
    : "Select Date Range";
}

export function createMyTaskStaticRanges() {
  return createStaticRanges([
    {
      label: "Last Week",
      range: () => ({
        startDate: moment().subtract(1, "weeks").startOf("week").toDate(),
        endDate: moment().subtract(1, "weeks").endOf("week").toDate(),
      }),
    },
    {
      label: "Last 7 Days",
      range: () => ({
        startDate: moment().subtract(7, "days").toDate(),
        endDate: moment().toDate(),
      }),
    },
    {
      label: "Last 14 Days",
      range: () => ({
        startDate: moment().subtract(14, "days").toDate(),
        endDate: moment().toDate(),
      }),
    },
    {
      label: "Last 30 Days",
      range: () => ({
        startDate: moment().subtract(30, "days").toDate(),
        endDate: moment().toDate(),
      }),
    },
    {
      label: "Custom",
      range: () => ({
        startDate: new Date(new Date().setDate(new Date().getDate() - 3)),
        endDate: new Date(new Date().setDate(new Date().getDate() - 3)),
      }),
      hasCustomRendering: true,
    },
  ]);
}

export function getMatchingStaticRangeLabel(date, customStaticRanges) {
  if (!date) {
    return "";
  }

  const allRanges = [...defaultStaticRanges, ...customStaticRanges];
  const matchedRange = allRanges.find(
    (range) =>
      moment(range.range().startDate).isSame(moment(date?.startDate), "day") &&
      moment(range.range().endDate).isSame(moment(date?.endDate), "day")
  );

  return matchedRange?.label || "";
}

export function filterWorklogsBySearchText(totalWorklogs = [], searchText = "") {
  if (!searchText) {
    return totalWorklogs;
  }

  const normalizedQuery = searchText.toLowerCase();

  return totalWorklogs.filter((cardData) => {
    return (
      cardData?.key?.toLowerCase().includes(normalizedQuery) ||
      cardData?.cardlog?.card?.card_id
        ?.toString()
        .includes(normalizedQuery) ||
      cardData?.cardlog?.user?.first_name
        ?.toLowerCase()
        .includes(normalizedQuery) ||
      cardData?.cardlog?.user?.last_name
        ?.toLowerCase()
        .includes(normalizedQuery) ||
      cardData?.cardlog?.card?.title
        ?.toString()
        .toLowerCase()
        .includes(normalizedQuery) ||
      cardData?.cardlog?.work_performed
        ?.toString()
        .toLowerCase()
        .includes(normalizedQuery) ||
      cardData?.epic_name?.toLowerCase().includes(normalizedQuery)
    );
  });
}

export function getPlannerFilteredTasks(tasks = [], filter = "All") {
  return [...tasks]
    .filter((task) => {
      if (filter === "All") {
        return true;
      }

      if (filter === "Assigned to me") {
        return Boolean(task.source_id);
      }

      if (filter === "Mine") {
        return !Boolean(task.source_id);
      }

      if (filter === "Completed") {
        return Boolean(task.completed_at);
      }

      return true;
    })
    .sort((a, b) => {
      const aCompleted = Boolean(a.completed_at);
      const bCompleted = Boolean(b.completed_at);
      return aCompleted === bCompleted ? 0 : aCompleted ? 1 : -1;
    });
}

export function getPlannerVisibleTasks(tasks = [], inputValue = "") {
  if (!inputValue) {
    return tasks;
  }

  const normalizedQuery = inputValue.toLowerCase();

  return tasks.filter((task) =>
    task?.title?.toLowerCase().includes(normalizedQuery)
  );
}

export function getWorklogUsers({
  isMobile = false,
  searchParams,
  groupingUser = [],
}) {
  if (isMobile) {
    return parseUserIdsParam(searchParams.get("uid"));
  }

  return getFlattenedGroupingUsers(groupingUser);
}

export function getWorklogBoards({
  isMobile = false,
  searchParams,
  boardList = [],
}) {
  if (isMobile) {
    return parseBoardIdsParam(searchParams.get("board"));
  }

  return boardList;
}

export function parseWorklogDateRange(dateParam) {
  if (!dateParam) {
    return null;
  }

  const parsedDate = JSON.parse(dateParam);

  return {
    startDate: new Date(parsedDate.startDate),
    endDate: new Date(parsedDate.endDate),
    key: "selection",
  };
}

export function shouldShowWorklogFilters(searchParams) {
  return Boolean(searchParams.get("uid") || searchParams.get("board"));
}

export function getWorklogRequestParams({
  store,
  fallbackBoardId = null,
}) {
  const currentState = store.getState();
  const currentBoardSearch = currentState.search.boardSearch;
  const currentGroupingUser = currentState.groupingUser.allocationUsers;

  return {
    boards:
      currentBoardSearch.length > 0
        ? currentBoardSearch
        : fallbackBoardId
        ? [fallbackBoardId]
        : [],
    users: currentGroupingUser[0]?.users || [],
  };
}

export function getTotalWorklogHours(worklogs = []) {
  return worklogs.reduce(
    (accumulator, currentValue) =>
      accumulator + (currentValue?.cardlog?.hours_spent || 0),
    0
  );
}

export function formatNoteEditorDate(createdAt) {
  if (!createdAt) {
    return "";
  }

  return new Date(createdAt).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
}

export function downloadCsvFile(text, filename = "worklog.csv") {
  const blob = new Blob([text], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.style.display = "none";
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export { defaultStaticRanges };
